import { Behavior } from "../Behavior";
import type { Particle } from "../Particle";
import type { IGrid } from "../types";

export class ChemicalReaction extends Behavior {
    reactants: string[];
    resultFactory: () => Particle;
    chance: number;

    /**
     * @param reactants 需要与之反应的其他粒子名称列表
     * @param resultFactory 生成的新粒子工厂函数
     * @param chance 反应发生的概率 (0-1)
     */
    constructor(reactants: string[], resultFactory: () => Particle, chance: number = 0.05) {
        super();
        this.reactants = reactants;
        this.resultFactory = resultFactory;
        this.chance = chance;
    }

    update(particle: Particle, grid: IGrid, index: number): number | void {
        if (Math.random() > this.chance) return;

        const width = grid.width;
        // Search 3x3 area
        const offsets = [
            -width - 1, -width, -width + 1,
            -1, 1,
            width - 1, width, width + 1
        ];

        const foundIndices: number[] = [];
        const reactantsNeeded = [...this.reactants];

        for (const offset of offsets) {
            const neighborIndex = index + offset;
            
            // Basic bounds check
            if (neighborIndex < 0 || neighborIndex >= grid.pixels.length) continue;

            // X-wrapping check
            const col = index % width;
            const neighborCol = neighborIndex % width;
            if (Math.abs(col - neighborCol) > 1) continue;

            const neighbor = grid.pixels[neighborIndex];
            
            // 检查邻居是否是所需的反应物之一
            // 注意：这里简单的使用 indexOf，意味着如果 reactants 有重复元素（如需要2个水），
            // 这个逻辑也支持（因为每次找到一个就 splice 一个）
            const reactantIndex = reactantsNeeded.indexOf(neighbor.name);
            
            if (reactantIndex !== -1) {
                foundIndices.push(neighborIndex);
                reactantsNeeded.splice(reactantIndex, 1);
                
                if (reactantsNeeded.length === 0) break;
            }
        }

        if (reactantsNeeded.length === 0) {
            // 找到所有反应物，触发反应
            
            // 计算平均温度，保持能量守恒（近似）
            let totalTemp = particle.temperature;
            for (const idx of foundIndices) {
                totalTemp += grid.pixels[idx].temperature;
            }
            const avgTemp = totalTemp / (foundIndices.length + 1);

            // 1. 转化邻居
            for (const idx of foundIndices) {
                const newP = this.resultFactory();
                newP.temperature = avgTemp;
                newP.updated = true; // 防止同一帧再次更新
                grid.setIndex(idx, newP);
            }
            
            // 2. 转化自己
            const newSelf = this.resultFactory();
            newSelf.temperature = avgTemp;
            newSelf.updated = true;
            grid.setIndex(index, newSelf);
            
            // 返回 -1 表示当前粒子已被销毁/转化，停止后续行为
            return -1;
        }
    }
}
